import React, { useState } from "react";
import { Form, Input, Button, Typography, Steps } from "antd";
import OtpInput from "react-otp-input";
import {
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../store/context/theme-context";
import { useTenant } from "../../store/context/tenant-context";
import { useNotification } from "../../store/context/notification-context";
import { useApiRequests } from "../../utils/api/https/executor";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";

const { Title, Text, Link } = Typography;
const { Step } = Steps;

const ForgotPasswordPageNew = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();
  const {
    initiateResetPassword,
    validateOTP: verifyOtp,
    completePasswordReset: resetPassword,
  } = useApiRequests();

  const isDark = theme === "dark";
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [resetData, setResetData] = useState({
    emailAddress: "",
    requestId: null,
    userId: null,
    otpCode: "",
  });

  // Step 1: Request Reset
  const handleRequestReset = async (values) => {
    try {
      setIsLoading(true);
      const response = await initiateResetPassword({ emailAddress: values.email });

      if (response?.ok && response?.outData?.data) {
        const { requestId, userId, emailAddress } = response.outData.data;
        setResetData({ ...resetData, requestId, userId, emailAddress });

        triggerNotification({
          type: "success",
          message: "Code Sent",
          description: "A verification code has been sent to your email.",
        });
        setCurrentStep(1);
      } else {
        triggerNotification({
          type: "error",
          message: "Request Failed",
          description: response?.outData?.message || "Failed to initiate password reset.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (values) => {
    try {
      setIsLoading(true);
      const otpCode = values.otp;

      const response = await verifyOtp({
        requestId: resetData.requestId,
        userId: resetData.userId,
        emailAddress: resetData.emailAddress,
        otp: otpCode,
      });

      if (response?.ok) {
        setResetData({ ...resetData, otpCode });
        triggerNotification({
          type: "success",
          message: "Verification Successful",
          description: "You can now reset your password.",
        });
        setCurrentStep(2);
      } else {
        triggerNotification({
          type: "error",
          message: "Verification Failed",
          description: response?.outData?.message || "Invalid verification code.",
        });
      }
    } catch (error) {
      console.log("??? error", error);
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred during verification.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (values) => {
    try {
      setIsLoading(true);
      const response = await resetPassword({
        requestId: resetData.requestId,
        userId: resetData.userId,
        emailAddress: resetData.emailAddress,
        newPassword: values.password,
      });

      if (response?.ok) {
        triggerNotification({
          type: "success",
          message: "Password Reset Successful",
          description: "You can now login with your new password.",
        });
        setCurrentStep(3);
        setTimeout(() => navigate("/authentication/login"), 2000);
      } else {
        triggerNotification({
          type: "error",
          message: "Reset Failed",
          description: response?.outData?.message || "Failed to reset password.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "An error occurred while resetting password.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <Title
              level={2}
              style={{
                margin: "0 0 8px 0",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "28px",
                fontWeight: 600,
              }}
            >
              Forgot Password?
            </Title>
            <Text
              style={{
                color: isDark ? "#a0a0a0" : "#595959",
                fontSize: "15px",
                display: "block",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              Enter your email address and we'll send you a verification code to reset your password.
            </Text>

            <Form form={form} onFinish={handleRequestReset} layout="vertical">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: isDark ? "#8c8c8c" : "#bfbfbf" }} />}
                  placeholder="Enter your email address"
                  style={{
                    height: "48px",
                    fontSize: "15px",
                    borderRadius: "8px",
                    backgroundColor: isDark ? "#262626" : "#ffffff",
                    borderColor: isDark ? "#434343" : "#d9d9d9",
                    color: isDark ? "#ffffff" : "#000000",
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  marginTop: "8px",
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                }}
              >
                Send Verification Code
              </Button>
            </Form>
          </>
        );

      case 1:
        return (
          <>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: `${tenant.theme.colorPrimary}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <SafetyOutlined style={{ fontSize: "32px", color: tenant.theme.colorPrimary }} />
            </div>

            <Title
              level={2}
              style={{
                margin: "0 0 8px 0",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "28px",
                fontWeight: 600,
              }}
            >
              Enter Verification Code
            </Title>
            <Text
              style={{
                color: isDark ? "#a0a0a0" : "#595959",
                fontSize: "15px",
                display: "block",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              We've sent a 6-digit code to{" "}
              <strong style={{ color: tenant.theme.colorPrimary }}>{resetData.emailAddress}</strong>
            </Text>

            <Form form={form} onFinish={handleVerifyOTP} layout="vertical">
<Form.Item
                name="otp"
                rules={[
                  { required: true, message: "Please enter the verification code." },
                  { len: 6, message: "The verification code must be 6 digits." },
                ]}
                style={{ marginBottom: "24px" }}
              >
                <OtpInput
                  numInputs={6}
                  inputType="tel"
                  shouldAutoFocus
                  renderInput={(props) => (
                    <input
                      {...props}
                      style={{
                        width: "100%",
                        minWidth: 0,
                        height: "56px",
                        fontSize: "24px",
                        textAlign: "center",
                        fontWeight: 600,
                        borderRadius: "8px",
                        border: `1px solid ${isDark ? "#434343" : "#d9d9d9"}`,
                        backgroundColor: isDark ? "#262626" : "#ffffff",
                        color: isDark ? "#ffffff" : "#000000",
                        outlineColor: tenant.theme.colorPrimary,
                      }}
                    />
                  )}
                  containerStyle={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                    columnGap: "12px",
                    width: "100%",
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                }}
              >
                Verify Code
              </Button>
            </Form>
          </>
        );

      case 2:
        return (
          <>
            <Title
              level={2}
              style={{
                margin: "0 0 8px 0",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "28px",
                fontWeight: 600,
              }}
            >
              Create New Password
            </Title>
            <Text
              style={{
                color: isDark ? "#a0a0a0" : "#595959",
                fontSize: "15px",
                display: "block",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              Please create a strong password for your account.
            </Text>

            <Form form={form} onFinish={handleResetPassword} layout="vertical">
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your new password" },
                  { min: 8, message: "Password must be at least 8 characters" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: isDark ? "#8c8c8c" : "#bfbfbf" }} />}
                  placeholder="New password"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  style={{
                    height: "48px",
                    fontSize: "15px",
                    borderRadius: "8px",
                    backgroundColor: isDark ? "#262626" : "#ffffff",
                    borderColor: isDark ? "#434343" : "#d9d9d9",
                  }}
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: isDark ? "#8c8c8c" : "#bfbfbf" }} />}
                  placeholder="Confirm new password"
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                  style={{
                    height: "48px",
                    fontSize: "15px",
                    borderRadius: "8px",
                    backgroundColor: isDark ? "#262626" : "#ffffff",
                    borderColor: isDark ? "#434343" : "#d9d9d9",
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                style={{
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  marginTop: "8px",
                  backgroundColor: tenant.theme.colorPrimary,
                  borderColor: tenant.theme.colorPrimary,
                }}
              >
                Reset Password
              </Button>
            </Form>
          </>
        );

      case 3:
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: `${tenant.theme.colorPrimary}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <CheckCircleOutlined style={{ fontSize: "48px", color: tenant.theme.colorPrimary }} />
            </div>

            <Title
              level={2}
              style={{
                margin: "0 0 12px 0",
                color: isDark ? "#ffffff" : "#000000",
                fontSize: "28px",
                fontWeight: 600,
              }}
            >
              Password Reset Successfully!
            </Title>
            <Text
              style={{
                color: isDark ? "#a0a0a0" : "#595959",
                fontSize: "15px",
                display: "block",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              Your password has been reset successfully. You'll be redirected to the login page shortly.
            </Text>

            <Button
              type="primary"
              onClick={() => navigate("/authentication/login")}
              style={{
                height: "48px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "8px",
                backgroundColor: tenant.theme.colorPrimary,
                borderColor: tenant.theme.colorPrimary,
                paddingLeft: "48px",
                paddingRight: "48px",
              }}
            >
              Go to Login
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isDark
          ? `linear-gradient(135deg, #1a1a2e 0%, ${tenant.theme.colorPrimary}40 50%, #16213e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}85 0%, ${tenant.theme.colorPrimary}95 100%)`,
        position: "relative",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Decorative Elements */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${tenant.theme.colorPrimary}10 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${tenant.theme.colorPrimary}15 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      {/* Top Bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === 'development' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1') && (
            <TenantSwitcher />
          )}
        <LanguageSelector />
      </div>

      {/* Main Card */}
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          backgroundColor: isDark ? "rgba(31, 31, 31, 0.95)" : "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          padding: "56px 48px",
          boxShadow: isDark
            ? "0 30px 80px rgba(0, 0, 0, 0.5)"
            : "0 30px 80px rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(10px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Back Button */}
        {currentStep < 3 && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate("/authentication/login")}
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              color: isDark ? "#ffffff" : "#000000",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            Back
          </Button>
        )}

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img
            src={isDark ? tenant.darkLogo : tenant.logo}
            alt={tenant.name}
            style={{
              maxWidth: "100px",
              height: "auto",
              marginBottom: "20px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              filter: isDark ? "brightness(1.1)" : "none",
            }}
          />
        </div>

        {/* Progress Steps */}
        {currentStep < 3 && (
          <Steps current={currentStep} style={{ marginBottom: "40px" }}>
            <Step title="Email" />
            <Step title="Verify" />
            <Step title="Reset" />
          </Steps>
        )}

        {/* Step Content */}
        <div>{renderStepContent()}</div>

        {/* Back to Login */}
        {currentStep < 3 && (
          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <Link
              onClick={() => navigate("/authentication/login")}
              style={{
                color: isDark ? "#8c8c8c" : "#595959",
                fontSize: "14px",
              }}
            >
              ← Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPageNew;
